import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { listOtherCharges } from "../erpClient";
import {
  extractListRowsFromResponse,
  parseApiPagination,
} from "./parseApiPagination";

const RATE_ON = new Set(["FIXED", "PCS", "CT", "WT"]);
/** Master data sometimes stores weight as GM/GMS instead of WT. */
const RATE_ON_ALIASES = {
  GM: "WT",
  GMS: "WT",
  GRAM: "WT",
  GRAMS: "WT",
};
let cachedOtherCharges = [];
let otherChargesLoaded = false;
let otherChargeLoadPromise = null;

function collectCodes(row) {
  const codes = [];
  [row?.code, row?.short_code].forEach((value) => {
    const parts = Array.isArray(value) ? value : String(value ?? "").split(",");
    parts.forEach((part) => {
      const code = String(part || "").trim();
      if (!code) return;
      if (codes.some((existing) => existing.toLowerCase() === code.toLowerCase())) {
        return;
      }
      codes.push(code);
    });
  });
  return codes;
}

function mapOtherChargeRow(row) {
  // API uses rate_on / rate; older payloads used price_to / price.
  const priceTo =
    row?.rate_on ?? row?.rateOn ?? row?.price_to ?? row?.priceTo ?? "";
  const price =
    row?.rate !== undefined && row?.rate !== null
      ? row.rate
      : row?.price !== undefined && row?.price !== null
        ? row.price
        : null;
  return {
    id: row?.id,
    name: String(row?.name || "").trim(),
    codes: collectCodes(row),
    priceTo: priceTo == null ? "" : String(priceTo).trim(),
    price,
  };
}

function isActiveCharge(row) {
  const status = row?.status;
  if (status === null || status === undefined || status === "") return true;
  return String(status) === "1";
}

/** Match short code first, then name. */
export function matchOtherCharges(charges, query) {
  const search = String(query || "").trim().toLowerCase();
  if (!search || !Array.isArray(charges)) return [];

  const exactCode = [];
  const codePrefix = [];
  const byName = [];

  charges.forEach((charge) => {
    const codes = (charge?.codes || [])
      .map((code) => String(code || "").trim().toLowerCase())
      .filter(Boolean);
    const name = String(charge?.name || "").toLowerCase();
    if (codes.some((code) => code === search)) exactCode.push(charge);
    else if (codes.some((code) => code.startsWith(search))) codePrefix.push(charge);
    else if (name.includes(search)) byName.push(charge);
  });

  return [...exactCode, ...codePrefix, ...byName];
}

/** Load active other-charge masters (all pages) for stock entry suggestions. */
export async function loadOtherChargeOptions() {
  const limit = 100;
  const rows = [];
  let page = 1;
  let totalPages = 1;

  do {
    const response = await listOtherCharges({ page, limit });
    if (response?.success === false) {
      throw new Error(response?.message || "Failed to load other charges");
    }
    const items = extractListRowsFromResponse(response, ["other_charges"]);
    if (Array.isArray(items)) rows.push(...items);
    totalPages = parseApiPagination(response, items.length).total_pages || 1;
    page += 1;
  } while (page <= totalPages && page <= 20);

  const options = rows
    .filter(isActiveCharge)
    .map(mapOtherChargeRow)
    .filter((row) => row.name);
  cachedOtherCharges = options;
  otherChargesLoaded = true;
  return options;
}

/** Fetch the charge list once. Later calls reuse the same result. */
export function ensureOtherChargeOptionsLoaded() {
  if (otherChargeLoadPromise) return otherChargeLoadPromise;
  if (otherChargesLoaded) {
    otherChargeLoadPromise = Promise.resolve(cachedOtherCharges);
    return otherChargeLoadPromise;
  }
  otherChargeLoadPromise = loadOtherChargeOptions().catch((error) => {
    otherChargeLoadPromise = null;
    console.error("Error fetching other charges:", error);
    toast.error(
      error?.response?.data?.message ||
        error?.message ||
        "Failed to load other charges",
    );
    throw error;
  });
  return otherChargeLoadPromise;
}

export function queryHasTwoLetters(query) {
  return (String(query || "").match(/[a-zA-Z]/g) || []).length >= 2;
}

export function getCachedOtherChargeOptions() {
  return cachedOtherCharges;
}

export function useOtherChargeOptions() {
  const [options, setOptions] = useState(cachedOtherCharges);

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      loadOtherChargeOptions()
        .then((rows) => {
          if (!cancelled) setOptions(rows);
        })
        .catch((error) => {
          console.error("Error fetching other charges:", error);
          if (!cancelled) {
            toast.error(
              error?.response?.data?.message ||
                error?.message ||
                "Failed to load other charges",
            );
          }
        });

    load();
    const handleApiUrlChange = () => {
      load();
    };
    window.addEventListener("apiUrlChanged", handleApiUrlChange);
    return () => {
      cancelled = true;
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, []);

  return options;
}

/** Loads other charges only when `ensureLoaded` runs, and only once. */
export function useLazyOtherChargeOptions() {
  const [options, setOptions] = useState(cachedOtherCharges);

  const ensureLoaded = useCallback(() => {
    ensureOtherChargeOptionsLoaded()
      .then((rows) => setOptions((prev) => (prev === rows ? prev : rows)))
      .catch(() => {});
  }, []);

  return [options, ensureLoaded];
}

/** Map a charge name to its short code from the loaded master list. */
export function getChargeTypeCode(name, charges) {
  if (!name) return "";
  const list =
    Array.isArray(charges) && charges.length ? charges : cachedOtherCharges;
  const n = String(name).toLowerCase().trim();
  const exact = list.find((charge) => String(charge.name).toLowerCase() === n);
  if (exact?.codes?.[0]) return exact.codes[0];
  const firstWord = (value) => String(value).toLowerCase().split(/\s+/)[0];
  const partial = list.find(
    (charge) =>
      firstWord(charge.name) === n ||
      String(charge.name).toLowerCase().startsWith(n),
  );
  return partial?.codes?.[0] ?? name;
}

/** On charge selection, set the name, copy rate into rate, and rate_on into rate on. */
export function applyOtherChargeToEntry(entry, charges, chargeName) {
  const raw = String(chargeName || "")
    .split(" (")[0]
    .trim();
  const lower = raw.toLowerCase();
  const list = charges || [];
  const match =
    list.find((charge) => String(charge.name).toLowerCase() === lower) ||
    list.find((charge) =>
      (charge.codes || []).some(
        (code) => String(code).trim().toLowerCase() === lower,
      ),
    );
  const next = { ...(entry || {}), chargeType: match?.name || raw };
  if (!match) return next;

  let rateOn = String(match.priceTo || "").trim().toUpperCase();
  rateOn = RATE_ON_ALIASES[rateOn] || rateOn;
  if (RATE_ON.has(rateOn)) next.rateOn = rateOn;

  const hasPrice =
    match.price !== null &&
    match.price !== undefined &&
    String(match.price).trim() !== "" &&
    Number.isFinite(Number(match.price));
  if (hasPrice) next.rate = String(match.price);
  return next;
}
